import React, { useState, useEffect } from 'react';

interface NewsItem {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  published_at: string;
  image_url?: string;
  category: string;
}

const ADMIN_PASSWORD = 'C121OZZaRd3t5WfJ'; // ← СМЕНИ ПАРОЛЬ!

const AdminNewsPage: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingNews, setEditingNews] = useState<NewsItem | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    published_at: new Date().toISOString().split('T')[0],
    category: 'Products',
    image_url: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');

  // Проверка логина
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsLoggedIn(true);
      localStorage.setItem('admin_logged_in', 'true');
      setLoginError('');
    } else {
      setLoginError('Неверный пароль');
    }
  };

  // Выход
  const handleLogout = () => {
    setIsLoggedIn(false);
    localStorage.removeItem('admin_logged_in');
  };

  // Проверка авторизации при загрузке
  useEffect(() => {
    const loggedIn = localStorage.getItem('admin_logged_in') === 'true';
    setIsLoggedIn(loggedIn);
  }, []);

  // Загрузка новостей
  const fetchNews = async () => {
    try {
      const res = await fetch('/api/news.php');
      const data = await res.json();
      setNews(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchNews();
    }
  }, [isLoggedIn]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Добавление / Редактирование
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage('');

    const url = editingNews 
      ? `/api/update-news.php?id=${editingNews.id}` 
      : '/api/add-news.php';

    const method = editingNews ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const result = await res.json();

      if (result.success) {
        setMessage(editingNews ? 'Новость обновлена!' : 'Новость добавлена!');
        resetForm();
        fetchNews();
      } else {
        setMessage('Ошибка при сохранении');
      }
    } catch (error) {
      setMessage('Ошибка соединения');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      excerpt: '',
      content: '',
      published_at: new Date().toISOString().split('T')[0],
      category: 'Products',
      image_url: '',
    });
    setEditingNews(null);
  };

  // Редактирование
  const handleEdit = (item: NewsItem) => {
    setEditingNews(item);
    setFormData({
      title: item.title,
      excerpt: item.excerpt,
      content: item.content,
      published_at: item.published_at,
      category: item.category,
      image_url: item.image_url || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Удаление
  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить эту новость?')) return;

    try {
      await fetch(`/api/delete-news.php?id=${id}`, { method: 'DELETE' });
      fetchNews();
    } catch (error) {
      alert('Ошибка при удалении');
    }
  };

  // === ЭКРАН ЛОГИНА ===
  if (!isLoggedIn) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f8f1e9 0%, #fff 100%)'
      }}>
        <div style={{ 
          background: 'white', 
          padding: '50px 60px', 
          borderRadius: '16px', 
          boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          width: '100%',
          maxWidth: '400px'
        }}>
          <h2 style={{ textAlign: 'center', marginBottom: '30px' }}>Admin Panel</h2>
          
          {loginError && <p style={{ color: '#e74c3c', textAlign: 'center' }}>{loginError}</p>}
          
          <form onSubmit={handleLogin}>
            <input
              type="password"
              placeholder="Enter admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ width: '100%', padding: '14px', fontSize: '16px', marginBottom: '20px' }}
              required
            />
            <button type="submit" className="btn-primary" style={{ width: '100%', padding: '14px' }}>
              Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  // === ОСНОВНОЙ ИНТЕРФЕЙС АДМИНКИ ===
  return (
    <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1>News Admin</h1>
        <button onClick={handleLogout} className="btn-primary">Logout</button>
      </div>

      {/* Форма */}
      <div style={{ background: 'white', padding: '35px', borderRadius: '16px', marginBottom: '50px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
        <h2 style={{ marginBottom: '25px' }}>{editingNews ? 'Edit News' : 'Add New Article'}</h2>

        {message && <p style={{ color: '#ff6b00', marginBottom: '20px', fontWeight: 500 }}>{message}</p>}

        <form onSubmit={handleSubmit}>
          {/* Поля формы */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '20px' }}>
            <input type="text" name="title" placeholder="Title" value={formData.title} onChange={handleChange} required />
            <input type="date" name="published_at" value={formData.published_at} onChange={handleChange} required />
          </div>

          <textarea name="excerpt" placeholder="Short excerpt" value={formData.excerpt} onChange={handleChange} required rows={2} style={{ width: '100%', marginBottom: '15px' }} />
          <textarea name="content" placeholder="Full content" value={formData.content} onChange={handleChange} required rows={7} style={{ width: '100%', marginBottom: '15px' }} />

          <div style={{ display: 'flex', gap: '20px' }}>
            <select name="category" value={formData.category} onChange={handleChange} style={{ flex: 1 }}>
              <option value="Products">Products</option>
              <option value="Research">Research</option>
              <option value="Team">Team</option>
            </select>
            <input type="text" name="image_url" placeholder="Image URL" value={formData.image_url} onChange={handleChange} style={{ flex: 2 }} />
          </div>

          <div style={{ marginTop: '25px', display: 'flex', gap: '15px' }}>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? 'Saving...' : editingNews ? 'Update News' : 'Add News'}
            </button>
            {editingNews && (
              <button type="button" onClick={resetForm} style={{ background: '#ccc', color: '#333' }} className="btn-primary">
                Cancel Edit
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Таблица новостей */}
      <h2 style={{ marginBottom: '20px' }}>All News ({news.length})</h2>

      {loading ? <p>Loading...</p> : (
        <table style={{ width: '100%', background: 'white', borderRadius: '12px', overflow: 'hidden' }}>
          <thead style={{ background: '#f8f1e9' }}>
            <tr>
              <th style={{ padding: '18px', textAlign: 'left' }}>Title</th>
              <th style={{ padding: '18px' }}>Date</th>
              <th style={{ padding: '18px' }}>Category</th>
              <th style={{ padding: '18px', width: '180px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {news.map((item) => (
              <tr key={item.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                <td style={{ padding: '18px' }}>{item.title}</td>
                <td style={{ padding: '18px', textAlign: 'center' }}>{item.published_at}</td>
                <td style={{ padding: '18px', textAlign: 'center' }}>{item.category}</td>
                <td style={{ padding: '18px', textAlign: 'center' }}>
                  <button onClick={() => handleEdit(item)} style={{ marginRight: '10px' }} className="btn-primary">Edit</button>
                  <button onClick={() => handleDelete(item.id)} style={{ background: '#e74c3c' }} className="btn-primary">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminNewsPage;